/**
 * Language Context
 * Provides language state and switching functionality with persistence
 * @module contexts/LanguageContext
 */

import type React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../locales/i18n';

const LANGUAGE_STORAGE_KEY = 'theychat-language';

interface LanguageContextType {
  /** Current language code */
  currentLanguage: SupportedLanguage;
  /** Whether the current language is RTL */
  isRTL: boolean;
  /** Change the app language */
  changeLanguage: (language: SupportedLanguage) => void;
  /** List of supported languages */
  supportedLanguages: typeof SUPPORTED_LANGUAGES;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<SupportedLanguage>(
    (i18n.language?.split('-')[0] as SupportedLanguage) || 'en',
  );

  const isRTL = SUPPORTED_LANGUAGES[currentLanguage]?.isRTL ?? false;

  // Sync with i18n language changes
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      const lang = lng.split('-')[0] as SupportedLanguage;
      if (lang in SUPPORTED_LANGUAGES) {
        setCurrentLanguage(lang);
        // Only update the lang attribute, NOT the dir
        // We keep layout LTR but add a class for RTL text styling
        document.documentElement.lang = lang;
        // Add/remove RTL class for selective text direction
        if (SUPPORTED_LANGUAGES[lang].isRTL) {
          document.body.classList.add('lang-rtl');
        } else {
          document.body.classList.remove('lang-rtl');
        }
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);

    // Set initial direction
    handleLanguageChanged(i18n.language || 'en');

    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);

  const changeLanguage = useCallback(
    (language: SupportedLanguage) => {
      i18n.changeLanguage(language);
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    },
    [i18n],
  );

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        isRTL,
        changeLanguage,
        supportedLanguages: SUPPORTED_LANGUAGES,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
